import { IElements, IFootballConfig, ITopScorersConfig, ITopScorersDataLabelsKeys, ITopScorersResponse } from '../../../../packages/types';
import { getLabelValue, removeUndefinedPropertiesFromObject } from '../../../../packages/utils';
import { INormalizedTopScorersData } from '../types';
import { normalizeTopScorer } from './normalizeTopScorer';

interface INormalizeTopScorersDataArgs {
  topScorers: ITopScorersResponse[];
  dataEntityLinks?: IFootballConfig['dataEntityLinks'];
  dataLabels?: ITopScorersConfig['dataLabels'];
  dataTeamNameType?: ITopScorersConfig['dataTeamNameType'];
  dataHighlightEntities?: ITopScorersConfig['dataHighlightEntities'];
  dataElements?: IElements;
  displayImage?: ITopScorersConfig['dataImageDisplay'];
}

const ALLOWED_TOP_SCORER_ELEMENTS: string[] = [
  ITopScorersDataLabelsKeys.ASSISTS,
  ITopScorersDataLabelsKeys.GOALS,
  ITopScorersDataLabelsKeys.MINUTES,
  ITopScorersDataLabelsKeys.PENALTIES,
  ITopScorersDataLabelsKeys.MISSED_PENALTIES,
  ITopScorersDataLabelsKeys.PLAYED,
  ITopScorersDataLabelsKeys.RED_CARDS,
  ITopScorersDataLabelsKeys.SCORED_FIRST,
  ITopScorersDataLabelsKeys.YELLOW_CARDS,
];

const columnIndicesPerElements: Record<string, number> = {
  [ITopScorersDataLabelsKeys.ASSISTS]: 1,
  [ITopScorersDataLabelsKeys.GOALS]: 2,
  [ITopScorersDataLabelsKeys.MINUTES]: 3,
  [ITopScorersDataLabelsKeys.PENALTIES]: 4,
  [ITopScorersDataLabelsKeys.MISSED_PENALTIES]: 5,
  [ITopScorersDataLabelsKeys.PLAYED]: 6,
  [ITopScorersDataLabelsKeys.RED_CARDS]: 7,
  [ITopScorersDataLabelsKeys.SCORED_FIRST]: 8,
  [ITopScorersDataLabelsKeys.YELLOW_CARDS]: 9,
};

export const normalizeTopScorers = ({
  topScorers,
  dataEntityLinks,
  dataLabels,
  dataTeamNameType,
  dataHighlightEntities,
  dataElements = [],
  displayImage,
}: INormalizeTopScorersDataArgs): INormalizedTopScorersData | undefined => {
  const elements = Array.isArray(dataElements)
    ? dataElements
    : (dataElements.stats as string[]) ?? [].filter((element) => ALLOWED_TOP_SCORER_ELEMENTS.includes(element));

  const defaultColumns = [
    { value: getLabelValue(dataLabels?.name, 'Name'), css: { flexGrow: '1' } },
    {
      value: getLabelValue(dataLabels?.assists_short, 'ASS'),
      tooltip: getLabelValue(dataLabels?.assists, 'Assists'),
    },
    {
      value: getLabelValue(dataLabels?.goals_short, 'G'),
      tooltip: getLabelValue(dataLabels?.goals, 'Goals'),
    },
    {
      value: getLabelValue(dataLabels?.minutes_short, 'M'),
      tooltip: getLabelValue(dataLabels?.minutes, 'Minutes'),
    },
    {
      value: getLabelValue(dataLabels?.penalties_short, 'P'),
      tooltip: getLabelValue(dataLabels?.penalties, 'Penalties'),
    },
    {
      value: getLabelValue(dataLabels?.missed_penalties_short, 'MP'),
      tooltip: getLabelValue(dataLabels?.missed_penalties, 'Missed penalties'),
    },
    {
      value: getLabelValue(dataLabels?.played_short, 'PL'),
      tooltip: getLabelValue(dataLabels?.played, 'Played'),
    },
    {
      value: getLabelValue(dataLabels?.red_cards_short, 'RC'),
      tooltip: getLabelValue(dataLabels?.red_cards, 'Red cards'),
    },
    {
      value: getLabelValue(dataLabels?.scored_first_short, 'SF'),
      tooltip: getLabelValue(dataLabels?.scored_first, 'Scored first'),
    },
    {
      value: getLabelValue(dataLabels?.yellow_cards_short, 'YC'),
      tooltip: getLabelValue(dataLabels?.yellow_cards, 'Yellow cards'),
    },
  ];

  const columns: typeof defaultColumns = [{ value: getLabelValue(dataLabels?.name, 'Name'), css: { flexGrow: '1' } }];

  elements.forEach((element) => {
    const defaultColumn = defaultColumns[columnIndicesPerElements[element]];

    defaultColumn && columns.push(defaultColumn);
  });

  const normalizedTopScorers =
    topScorers?.map((item: ITopScorersResponse) =>
      normalizeTopScorer({
        item,
        dataEntityLinks,
        dataHighlightEntities,
        dataTeamNameType,
        dataElements: elements,
        displayImage,
      }),
    ) ?? undefined;

  return removeUndefinedPropertiesFromObject({
    items: {
      data: [
        {
          item: {
            data: normalizedTopScorers,
            desktopColumns: elements.length === 0 ? defaultColumns : columns,
            mobileColumns: elements.length === 0 ? defaultColumns : columns,
          },
        },
      ],
    },
  });
};
